export type CareNavigationDimension =
  | "clinic"
  | "care_navigator"
  | "care_navigator_clinic"
  | "group"
  | "care_navigator_patient";

export type EncounterSplitMode = "all" | "split";

export type CalendarStatusFilter =
  | "all"
  | "needs_scheduling"
  | "scheduled_in_window"
  | "scheduled_future"
  | "stale_no_encounter"
  | "annual_assessment_scheduled"
  | "cancelled_only"
  | "completed_this_month";

export type CareNavigationFilters = {
  clinicIds?: string[];
  navigatorIds?: string[];
  groupIds?: string[];
};

export type CareNavigationTimeframe = {
  windowPreset: "daily" | "weekly" | "monthly" | "annual" | "custom";
  anchorDate: string;
  dateStart: string;
  dateEnd: string;
  timezone: string;
};

export type CareNavigationKpi = {
  key: string;
  label: string;
  value: number;
  display: string;
  delta?: number;
  denominator?: string;
  tooltip?: string;
};

export type CareNavigationSnapshotResponse = CareNavigationTimeframe & {
  cache?: "HIT" | "MISS" | "STALE";
  navigatorRows?: CareNavigationDrilldownRow[];
  groupRows?: CareNavigationDrilldownRow[];
  window?: string;
  trendBucket?: string;
  lastSnapshotAt: string;
  source: "live" | "mock" | string;
  kpis: CareNavigationKpi[];
  trends?: {
    encounter: Array<{ date: string; value: number; target: number }>;
    attrition: Array<Record<string, unknown>>;
  };
  cnDistribution?: Array<Record<string, unknown>>;
  clinicMatrix?: Array<Record<string, unknown>>;
};

export type CareNavigationDrilldownRow = {
  id?: string;
  name?: string;
  clinic?: string;
  navigator?: string;
  group?: string;
  patient_tier?: string | null;
  carespaces?: number;
  carespace_id?: string;
  carespace_url?: string | null;
  current_status?: string;
  [key: string]: unknown;
};

export type CareNavigationDrilldownResponse = CareNavigationTimeframe & {
  cache?: "HIT" | "MISS" | "STALE";
  dimension: CareNavigationDimension | string;
  metrics: string[];
  rows: CareNavigationDrilldownRow[];
  encounterSplitMode?: EncounterSplitMode | string;
  source?: "live" | "mock" | string;
};

export type CareNavigationCalendarRow = {
  carespace_id: string;
  carespace_url?: string | null;
  clinic?: string;
  navigator?: string;
  current_status?: string;
  calendar_status: string;
  event_title?: string | null;
  cal_uid?: string | null;
  cal_booking_url?: string | null;
  booking_start_at?: string | null;
  booking_end_at?: string | null;
  booking_status?: string | null;
  outreach_attempts_total?: number;
  needs_scheduling_this_month?: boolean;
  call_attempts?: number;
  weekly_attempts?: Record<string, { calls: number; texts: number; emails: number }>;
  text_attempts?: number;
  voicemail_attempts?: number;
  email_attempts?: number;
  [key: string]: unknown;
};

export type CareNavigationCalendarStatusResponse = CareNavigationTimeframe & {
  cache?: "HIT" | "MISS" | "STALE";
  rows: CareNavigationCalendarRow[];
  summary: Record<string, number>;
  source: "live" | "mock" | string;
};

export type CareNavigationDrilldownRequest = CareNavigationTimeframe & {
  dimension: CareNavigationDimension;
  filters?: CareNavigationFilters;
  metrics?: string[];
  encounterSplitMode?: EncounterSplitMode;
  includePreviousMonthBilling?: boolean;
};

export type CareNavigationCalendarStatusRequest = CareNavigationTimeframe & {
  filters?: CareNavigationFilters;
  status?: CalendarStatusFilter;
  limit?: number;
  offset?: number;
};

export function isCareNavigationSnapshotResponse(
  value: unknown,
): value is CareNavigationSnapshotResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Record<string, unknown>;
  return (
    typeof body.dateStart === "string" &&
    typeof body.dateEnd === "string" &&
    Array.isArray(body.kpis) &&
    typeof body.source === "string"
  );
}

export function isCareNavigationDrilldownResponse(
  value: unknown,
): value is CareNavigationDrilldownResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Record<string, unknown>;
  return (
    typeof body.dimension === "string" &&
    Array.isArray(body.metrics) &&
    Array.isArray(body.rows) &&
    typeof body.dateStart === "string"
  );
}

export function isCareNavigationCalendarStatusResponse(
  value: unknown,
): value is CareNavigationCalendarStatusResponse {
  if (!value || typeof value !== "object") return false;
  const body = value as Record<string, unknown>;
  return Array.isArray(body.rows) && typeof body.summary === "object" && body.summary != null;
}
